type Format = "jpg" | "jpeg" | "png" | "webp"; // supported output formats
type HexColor = string; // e.g. 'C0C0C0'

export class MedianovaImage {
  private domain: string | null = null; // e.g. 'youraccount.mncdn.com'
  private path: string | null = null; // path + filename, e.g. 'images/pic.jpg'
  private isRelative: boolean = false; // flag to indicate if the URL is relative
  private relativeUrl: string | null = null; // store original relative URL

  constructor(url: string) {
    // Check if the URL is relative (no protocol)
    if (!url.match(/^https?:\/\//)) {
      // It's a relative URL, store it as-is with no modifications
      this.isRelative = true;
      this.relativeUrl = url;
      return;
    }
    
    // Parse the URL to extract domain and path
    const parsedUrl = new URL(url);
    this.domain = parsedUrl.hostname;
    // Remove leading slash from pathname
    this.path = parsedUrl.pathname.replace(/^\/+/, "");
  }

  /** 1. Resize (MNRESIZE): width & height, use - to auto-calc */
  resize(width: number | "-", height: number | "-"): string {
    // Validate inputs
    if (typeof width !== "number" && width !== "-") return this.toUrl();
    if (typeof height !== "number" && height !== "-") return this.toUrl();
    if (typeof width === "number" && width <= 0) return this.toUrl();
    if (typeof height === "number" && height <= 0) return this.toUrl();
    
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      return `https://${this.domain}/mnresize/${width}/${height}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return this.toUrl();
  }

  /** 2. Padding (MNPADDING): requires width, height, hex color */
  padding(width: number, height: number, hexColor: HexColor): string {
    // Validate inputs
    if (width <= 0 || height <= 0) return this.toUrl();
    if (!hexColor.match(/^[0-9A-Fa-f]{6}$/)) return this.toUrl();
    
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      return `https://${this.domain}/mnpadding/${width}/${height}/${hexColor}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return this.toUrl();
  }

  /** 3. Crop center (MNCROP): exact width & height */
  crop(width: number, height: number): string {
    // Validate inputs
    if (width <= 0 || height <= 0) return this.toUrl();
    
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      return `https://${this.domain}/mncrop/${width}/${height}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return this.toUrl();
  }

  /** 4. Crop + Resize (MNCROPRESIZE) */
  cropResize(width: number, height: number): string {
    // Validate inputs
    if (width <= 0 || height <= 0) return this.toUrl();
    
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      return `https://${this.domain}/mncropresize/${width}/${height}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return this.toUrl();
  }

  /** 6. Rotate (MNROTATE): angle must be 90, 180 or 270 */
  rotate(degrees: 90 | 180 | 270): string {
    // Validate inputs - TypeScript will enforce the type, but we'll check anyway
    if (degrees !== 90 && degrees !== 180 && degrees !== 270) return this.toUrl();
    
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      return `https://${this.domain}/mnrotate/${degrees}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return this.toUrl();
  }

  /** 7. Watermark (MNWATERMARK): url-encode watermark path */
  watermark(wmPath: string): string {
    // Validate inputs
    if (!wmPath || typeof wmPath !== 'string') return this.toUrl();
    
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      const encoded = encodeURIComponent(wmPath.replace(/^\//, ""));
      return `https://${this.domain}/mnwatermark/${encoded}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return this.toUrl();
  }

  /** 8. Custom Crop & Resize (MNCUSTOMCROPRESIZE):
   *  x, y: top-left coords; w, h: crop area; tw, th: output dims
   */
  customCropResize(
    x: number,
    y: number,
    w: number,
    h: number,
    tw: number,
    th: number,
  ): string {
    // Validate inputs
    if (x < 0 || y < 0 || w <= 0 || h <= 0 || tw <= 0 || th <= 0) return this.toUrl();
    
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      return `https://${this.domain}/mncustomcropresize/${x}/${y}/${w}/${h}/${tw}/${th}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return this.toUrl();
  }

  /** Set explicit output format override (jpg, png, webp) */
  format(fmt: Format): string {
    // Validate inputs - TypeScript will enforce the type, but we'll check anyway
    if (!["jpg", "jpeg", "png", "webp"].includes(fmt)) return this.toUrl();
    
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      return `https://${this.domain}/${fmt}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return this.toUrl();
  }

  /** Build final URL without any operations */
  toUrl(): string {
    // If it's a relative URL, return it as-is with no modifications
    if (this.isRelative && this.relativeUrl) {
      return this.relativeUrl;
    }
    
    // For absolute URLs, build the transformed URL
    if (this.domain && this.path) {
      return `https://${this.domain}/${this.path}`;
    }
    
    // Fallback case (should not happen with proper usage)
    return '';
  }
}
